import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle,
  Ticket,
  FileText,
  Tag,
  AlertTriangle
} from 'lucide-react';
import { apiRequest } from '../../api/client';
import { toast } from '../../utils/toastUtils';
import { useI18n } from '../../i18n';

const getCategories = (t) => [
  { value: 'technical', label: t('support.categories.technical'), description: t('support.categories.technicalDesc') },
  { value: 'billing', label: t('support.categories.billing'), description: t('support.categories.billingDesc') },
  { value: 'feature_request', label: t('support.categories.feature_request'), description: t('support.categories.feature_requestDesc') },
  { value: 'bug_report', label: t('support.categories.bug_report'), description: t('support.categories.bug_reportDesc') },
  { value: 'account', label: t('support.categories.account'), description: t('support.categories.accountDesc') },
  { value: 'security', label: t('support.categories.security'), description: t('support.categories.securityDesc') },
  { value: 'other', label: t('support.categories.other'), description: t('support.categories.otherDesc') }
];

const getPriorities = (t) => [
  { value: 'low', label: t('support.priorities.low'), description: t('support.priorities.lowDesc'), color: 'text-gray-400' },
  { value: 'medium', label: t('support.priorities.medium'), description: t('support.priorities.mediumDesc'), color: 'text-blue-400' },
  { value: 'high', label: t('support.priorities.high'), description: t('support.priorities.highDesc'), color: 'text-orange-400' },
  { value: 'urgent', label: t('support.priorities.urgent'), description: t('support.priorities.urgentDesc'), color: 'text-red-400' }
];

const NewTicket = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [workspaces, setWorkspaces] = useState([]);

  const categories = getCategories(t);
  const priorities = getPriorities(t);

  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    category: 'technical',
    priority: 'medium',
    related_workspace_id: ''
  });

  const [errors, setErrors] = useState({});

  // Fetch workspaces for optional linking
  useEffect(() => {
    const fetchWorkspaces = async () => {
      try {
        const data = await apiRequest('/api/v1/workspaces?limit=100');
        setWorkspaces(data?.workspaces || data || []);
      } catch (err) {
        console.error('Error fetching workspaces:', err);
      }
    };
    fetchWorkspaces();
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.subject.trim()) {
      newErrors.subject = t('support.errors.subjectRequired');
    } else if (formData.subject.length < 5) {
      newErrors.subject = t('support.errors.subjectMinLength');
    }

    if (!formData.description.trim()) {
      newErrors.description = t('support.errors.descriptionRequired');
    } else if (formData.description.length < 20) {
      newErrors.description = t('support.errors.descriptionMinLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('support.errors.fixFormErrors'));
      return;
    }

    try {
      setLoading(true);

      const payload = {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        category: formData.category,
        priority: formData.priority
      };

      if (formData.related_workspace_id) {
        payload.related_workspace_id = formData.related_workspace_id;
      }

      const ticket = await apiRequest('/api/v1/support/tickets', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      toast.success(t('support.success.ticketCreated', { number: ticket.ticket_number }));
      navigate(`/support/tickets/${ticket.id}`);
    } catch (err) {
      console.error('Error creating ticket:', err);
      toast.error(err.message || t('support.errors.creatingTicket'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  return (
    <div className="mx-auto px-6 py-8 w-[80%]">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/support/tickets"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-brand-green transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('support.backToTickets')}
        </Link>

        <h1 className="text-3xl font-bold text-white flex items-center gap-3">
          <Ticket className="w-8 h-8 text-brand-green" />
          {t('support.newTicketTitle')}
        </h1>
        <p className="text-gray-400 mt-1">{t('support.newTicketDesc')}</p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Subject */}
        <div className="liquid-glass rounded-xl p-6">
          <label className="block text-white font-medium mb-2">
            <FileText className="w-4 h-4 inline mr-2 text-brand-green" />
            {t('support.subject')} *
          </label>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) => handleChange('subject', e.target.value)}
            placeholder={t('support.describeIssue')}
            className={`w-full px-4 py-3 bg-dark-800/50 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 ${
              errors.subject ? 'border-red-500' : 'border-brand-green/20'
            }`}
            maxLength={500}
          />
          {errors.subject && (
            <p className="mt-2 text-red-400 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.subject}
            </p>
          )}
          <p className="mt-2 text-gray-500 text-sm">{t('support.charactersCount', { count: `${formData.subject.length}/500` })}</p>
        </div>

        {/* Category & Priority */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Category */}
          <div className="liquid-glass rounded-xl p-6">
            <label className="block text-white font-medium mb-4">
              <Tag className="w-4 h-4 inline mr-2 text-brand-green" />
              {t('support.category')}
            </label>
            <div className="space-y-2">
              {categories.map((cat) => (
                <label
                  key={cat.value}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    formData.category === cat.value
                      ? 'bg-brand-green/10 border border-brand-green/30'
                      : 'bg-dark-800/30 border border-transparent hover:border-brand-green/20'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={formData.category === cat.value}
                    onChange={(e) => handleChange('category', e.target.value)}
                    className="mt-1 text-brand-green focus:ring-brand-green"
                  />
                  <div>
                    <span className="text-white font-medium">{cat.label}</span>
                    <p className="text-gray-500 text-sm">{cat.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Priority */}
          <div className="liquid-glass rounded-xl p-6">
            <label className="block text-white font-medium mb-4">
              <AlertTriangle className="w-4 h-4 inline mr-2 text-brand-green" />
              {t('support.priority')}
            </label>
            <div className="space-y-2">
              {priorities.map((pri) => (
                <label
                  key={pri.value}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-all ${
                    formData.priority === pri.value
                      ? 'bg-brand-green/10 border border-brand-green/30'
                      : 'bg-dark-800/30 border border-transparent hover:border-brand-green/20'
                  }`}
                >
                  <input
                    type="radio"
                    name="priority"
                    value={pri.value}
                    checked={formData.priority === pri.value}
                    onChange={(e) => handleChange('priority', e.target.value)}
                    className="mt-1 text-brand-green focus:ring-brand-green"
                  />
                  <div>
                    <span className={`font-medium ${pri.color}`}>{pri.label}</span>
                    <p className="text-gray-500 text-sm">{pri.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Related Workspace (optional) */}
        {workspaces.length > 0 && (
          <div className="liquid-glass rounded-xl p-6">
            <label className="block text-white font-medium mb-2">
              {t('support.relatedWorkspace')}
            </label>
            <select
              value={formData.related_workspace_id}
              onChange={(e) => handleChange('related_workspace_id', e.target.value)}
              className="w-full px-4 py-3 bg-dark-800 border border-brand-green/20 rounded-lg text-white focus:outline-none focus:border-brand-green/50"
              style={{ backgroundColor: '#1a1a2e' }}
            >
              <option value="" className="bg-dark-800 text-white" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>
                {t('support.noSpecificWorkspace')}
              </option>
              {workspaces.map((ws) => (
                <option key={ws.id} value={ws.id} className="bg-dark-800 text-white" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>
                  {ws.name || ws.company_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Description */}
        <div className="liquid-glass rounded-xl p-6">
          <label className="block text-white font-medium mb-2">
            {t('support.detailedDescription')} *
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder={t('support.detailedDescPlaceholder')}
            rows={8}
            className={`w-full px-4 py-3 border rounded-lg placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none ${
              errors.description ? 'border-red-500' : 'border-brand-green/20'
            }`}
            style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}
          />
          {errors.description && (
            <p className="mt-2 text-red-400 text-sm flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {errors.description}
            </p>
          )}
          <p className="mt-2 text-gray-500 text-sm">{t('support.charactersMin', { count: formData.description.length, min: 20 })}</p>
        </div>

        {/* Info Box */}
        <div className="bg-brand-green/5 border border-brand-green/20 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-brand-green mt-0.5" />
            <div>
              <p className="text-white font-medium">{t('support.responseTime')}</p>
              <p className="text-gray-400 text-sm mt-1">
                {t('support.responseTimeDesc')}
              </p>
            </div>
          </div>
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Link
            to="/support/tickets"
            className="px-6 py-3 text-gray-400 hover:text-white transition-colors"
          >
            {t('support.cancel')}
          </Link>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-8 py-3 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('support.creating')}
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                {t('support.createTicket')}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NewTicket;
